import { Controller } from "@hotwired/stimulus"

/**
 * Manages a horizontal scrolling carousel with optional navigation buttons.
 * Automatically updates button states based on scroll position.
 */
export default class extends Controller {
  static targets = ["track", "prevButton", "nextButton"]

  connect() {
    this._updateButtons()
    this.trackTarget.addEventListener("scroll", this._onScroll)
  }

  disconnect() {
    this.trackTarget.removeEventListener("scroll", this._onScroll)
  }

  /**
   * Scrolls the track to the previous page.
   */
  scrollPrevious() {
    this.scrollBy(-1)
  }

  /**
   * Scrolls the track to the next page.
   */
  scrollNext() {
    this.scrollBy(1)
  }

  /**
   * Scrolls the track by a calculated distance based on client width.
   * @param {number} direction - 1 for next, -1 for previous.
   */
  scrollBy(direction) {
    if (!this.hasTrackTarget) return

    const distance = Math.max(this.trackTarget.clientWidth * 0.85, 320)
    this.trackTarget.scrollBy({ left: distance * direction, behavior: "smooth" })
  }

  _onScroll = () => {
    this._updateButtons()
  }

  /**
   * Disables/enables navigation buttons based on whether the track is at the start or end.
   */
  _updateButtons() {
    if (!this.hasTrackTarget) return

    const { scrollLeft, scrollWidth, clientWidth } = this.trackTarget
    const atStart = scrollLeft <= 0
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1

    if (this.hasPrevButtonTarget) this._setDisabled(this.prevButtonTarget, atStart)
    if (this.hasNextButtonTarget) this._setDisabled(this.nextButtonTarget, atEnd)
  }

  /**
   * Updates button state and styling.
   * @param {HTMLElement} button
   * @param {boolean} disabled
   */
  _setDisabled(button, disabled) {
    button.disabled = disabled
    button.classList.toggle("opacity-30", disabled)
    button.classList.toggle("cursor-not-allowed", disabled)
  }
}
